import { format, isAfter, isValid, parse, parseISO } from "date-fns";
import { dateTimeFromDateObjectOrString, relativeDateIsValid } from "./dateUtility";
import type { CommonExpressionDefinition, RelativeDateConstraint } from "../types/Survey";
import type { ConsentSignatureResultModel, QuestionResultModel, ResultModel, StepResultModel, WebViewStepResultModel } from "../types/SurveyResult";

type AtLeastOne<T, Keys extends keyof T = keyof T> =
    Keys extends keyof T
    ? Required<Pick<T, Keys>> & Partial<Omit<T, Keys>>
    : never;

export type ManagedResultsLookup = Record<string, StepResultModel | undefined>;

export interface SurveyResultPredicateBase {
    resultIdentifier: string;
    stepIdentifier?: string;
}

export interface TruePredicate {
    type: "TruePredicate";
}

export interface FalsePredicate {
    type: "FalsePredicate";
}

export interface BooleanQuestionResultPredicate extends SurveyResultPredicateBase {
    type: "BooleanQuestionResultPredicate";
    expectedAnswer: boolean;
}

export interface ConsentPredicate extends SurveyResultPredicateBase {
    type: "ConsentPredicate";
    didConsent: boolean;
}

export interface WebViewResultPredicateExpectedString extends SurveyResultPredicateBase {
    type: "WebViewResultPredicate";
    expectedString: string;
    matchingPattern?: never;
}

export interface WebViewResultPredicateMatchingPattern extends SurveyResultPredicateBase {
    type: "WebViewResultPredicate";
    expectedString?: never;
    matchingPattern: string;
}

export type WebViewResultPredicate = WebViewResultPredicateExpectedString | WebViewResultPredicateMatchingPattern;

export interface NumericPredicateProperties {
    expectedAnswer?: number;
    minimumExpectedAnswerValue?: number;
    maximumExpectedAnswerValue?: number;
}

type RequiredNumericPredicateProperties = AtLeastOne<NumericPredicateProperties>;

export type NumericQuestionResultPredicate = SurveyResultPredicateBase & RequiredNumericPredicateProperties & {
    type: "NumericQuestionResultPredicate";
};

export type ScaleQuestionResultPredicate = SurveyResultPredicateBase & RequiredNumericPredicateProperties & {
    type: "ScaleQuestionResultPredicate";
};

export type HeightWeightQuestionResultPredicate = SurveyResultPredicateBase & RequiredNumericPredicateProperties & {
    type: "HeightWeightQuestionResultPredicate";
};

export interface TextQuestionResultPredicate extends SurveyResultPredicateBase {
    type: "TextQuestionResultPredicate";
    expectedString: string;
}

export interface ChoiceQuestionResultPredicateExpectedString extends SurveyResultPredicateBase {
    type: "ChoiceQuestionResultPredicate";
    expectedString: string;
    expectedStrings?: never;
}

export interface ChoiceQuestionResultPredicateExpectedStrings extends SurveyResultPredicateBase {
    type: "ChoiceQuestionResultPredicate";
    expectedString?: never;
    expectedStrings: string[];
}

export type ChoiceQuestionResultPredicate = ChoiceQuestionResultPredicateExpectedString | ChoiceQuestionResultPredicateExpectedStrings;

export interface DatePredicateProperties {
    minimumExpectedDate?: RelativeDateConstraint | string;
    maximumExpectedDate?: RelativeDateConstraint | string;
}

type RequiredDatePredicateProperties = AtLeastOne<DatePredicateProperties>;

export type DateQuestionResultPredicate = SurveyResultPredicateBase & RequiredDatePredicateProperties & {
    type: "DateQuestionResultPredicate";
};

export type TimeOfDayQuestionResultPredicate = SurveyResultPredicateBase & RequiredDatePredicateProperties & {
    type: "TimeOfDayQuestionResultPredicate";
};

export interface NilQuestionResultPredicate extends SurveyResultPredicateBase {
    type: "NilQuestionResultPredicate";
}

export interface CustomFieldPredicate {
    type: "CustomFieldPredicate";
    customField: string;
    operator: "IsEmpty" | "IsEqual";
    expectedValue?: string;
}

export interface InstitutionPredicate {
    type: "InstitutionPredicate";
    operator: "IsEmpty" | "IsEqual";
    expectedValue?: string;
}

export interface ExpressionPredicate {
    type: "ExpressionPredicate";
    expression: string;
}

export interface ParticipantsInSegmentPredicateProperties {
    minimumExpectedValue?: number;
    maximumExpectedValue?: number;
}

type RequiredParticipantsInSegmentPredicateProperties = AtLeastOne<ParticipantsInSegmentPredicateProperties>;

export type ParticipantsInSegmentPredicate = RequiredParticipantsInSegmentPredicateProperties & {
    type: "ParticipantsInSegmentPredicate";
    segment: string;
}

export interface CommonExpressionPredicate {
    type: "CommonExpressionPredicate";
    expression: CommonExpressionDefinition;
}

type DemographicStringValueField = "Gender" | "Email" | "City" | "State" | "PostalCode";
type DemographicEmptyOnlyField = "FirstName" | "MiddleName" | "LastName" | "MobilePhone" | "Street1" | "Street2" | "DateOfBirth";

export interface DemographicAgeIsEqualPredicate {
    type: "DemographicFieldPredicate";
    demographicField: "Age";
    operator: "IsEqual";
    expectedValue: number;
}

export interface DemographicAgeMinimumPredicate {
    type: "DemographicFieldPredicate";
    demographicField: "Age";
    operator: "Minimum";
    minimumExpectedValue: number;
}

export interface DemographicAgeMaximumPredicate {
    type: "DemographicFieldPredicate";
    demographicField: "Age";
    operator: "Maximum";
    maximumExpectedValue: number;
}

export interface DemographicAgeBetweenPredicate {
    type: "DemographicFieldPredicate";
    demographicField: "Age";
    operator: "Between";
    minimumExpectedValue: number;
    maximumExpectedValue: number;
}

export interface DemographicStringValuePredicate {
    type: "DemographicFieldPredicate";
    demographicField: DemographicStringValueField;
    operator: "IsEmpty" | "IsEqual";
    expectedValue?: string;
}

export interface DemographicEmptyOnlyPredicate {
    type: "DemographicFieldPredicate";
    demographicField: DemographicEmptyOnlyField;
    operator: "IsEmpty";
}

export type DemographicFieldPredicate =
    | DemographicAgeIsEqualPredicate
    | DemographicAgeMinimumPredicate
    | DemographicAgeMaximumPredicate
    | DemographicAgeBetweenPredicate
    | DemographicStringValuePredicate
    | DemographicEmptyOnlyPredicate;

export interface NotCompoundPredicate {
    type: "NotCompoundPredicate";
    subpredicate: SurveyPredicate;
}

export interface AndCompoundPredicate {
    type: "AndCompoundPredicate";
    subpredicates: SurveyPredicate[];
}

export interface OrCompoundPredicate {
    type: "OrCompoundPredicate";
    subpredicates: SurveyPredicate[];
}

export type SurveyPredicate =
    | TruePredicate
    | FalsePredicate
    | BooleanQuestionResultPredicate
    | ConsentPredicate
    | WebViewResultPredicate
    | NumericQuestionResultPredicate
    | ScaleQuestionResultPredicate
    | HeightWeightQuestionResultPredicate
    | TextQuestionResultPredicate
    | ChoiceQuestionResultPredicate
    | DateQuestionResultPredicate
    | TimeOfDayQuestionResultPredicate
    | NilQuestionResultPredicate
    | CustomFieldPredicate
    | DemographicFieldPredicate
    | InstitutionPredicate
    | ExpressionPredicate
    | ParticipantsInSegmentPredicate
    | CommonExpressionPredicate
    | NotCompoundPredicate
    | AndCompoundPredicate
    | OrCompoundPredicate;

function isQuestionResult(result: ResultModel | null): result is QuestionResultModel {
    return !!result && result.Type === "QuestionResult";
}

function isConsentSignatureResult(result: ResultModel | null): result is ConsentSignatureResultModel {
    return !!result && result.Type === "ConsentSignatureResult";
}

function isWebViewStepResult(result: ResultModel | null): result is WebViewStepResultModel {
    return !!result && result.Type === "WebViewStepResult";
}

function isBlankAnswer(value: string | null | undefined): boolean {
    return value === undefined || value === null || value === "";
}

function getResult(predicate: SurveyResultPredicateBase, managedResults: ManagedResultsLookup): ResultModel | null {
    const stepIdentifier = predicate.stepIdentifier ?? predicate.resultIdentifier;
    const stepResult = managedResults[stepIdentifier];
    if (!stepResult) {
        return null;
    }

    return stepResult.QuestionResults.find(result => result.Identifier === predicate.resultIdentifier) ?? null;
}

function evaluateNumericPredicate(predicate: NumericPredicateProperties & SurveyResultPredicateBase, managedResults: ManagedResultsLookup): boolean {
    const questionResult = getResult(predicate, managedResults);
    if (!isQuestionResult(questionResult)) {
        return false;
    }

    const answer = questionResult.Answers[0];
    if (isBlankAnswer(answer)) {
        return false;
    }

    const numericAnswer = Number(answer);
    if (!Number.isFinite(numericAnswer)) {
        return false;
    }

    if (predicate.expectedAnswer !== undefined && predicate.expectedAnswer !== numericAnswer) {
        return false;
    }
    if (predicate.maximumExpectedAnswerValue !== undefined && predicate.maximumExpectedAnswerValue < numericAnswer) {
        return false;
    }
    if (predicate.minimumExpectedAnswerValue !== undefined && predicate.minimumExpectedAnswerValue > numericAnswer) {
        return false;
    }

    return true;
}

function evaluateDatePredicate(predicate: DatePredicateProperties & SurveyResultPredicateBase, managedResults: ManagedResultsLookup): boolean {
    const questionResult = getResult(predicate, managedResults);
    if (!isQuestionResult(questionResult)) {
        return false;
    }

    const answer = questionResult.Answers[0];
    if (isBlankAnswer(answer)) {
        return false;
    }

    const parsedAnswerDate = parseISO(answer);
    if (!isValid(parsedAnswerDate)) {
        return false;
    }

    if (predicate.maximumExpectedDate !== undefined) {
        const maximumExpectedDate = dateTimeFromDateObjectOrString(predicate.maximumExpectedDate);
        if (maximumExpectedDate && isValid(parsedAnswerDate) && isAfter(parsedAnswerDate, maximumExpectedDate)) {
            return false;
        }
    }
    if (predicate.minimumExpectedDate !== undefined) {
        const minimumExpectedDate = dateTimeFromDateObjectOrString(predicate.minimumExpectedDate);
        if (minimumExpectedDate && isValid(parsedAnswerDate) && isAfter(minimumExpectedDate, parsedAnswerDate)) {
            return false;
        }
    }

    return true;
}

function parseTimeAnswer(value: string): Date | undefined {
    const parsed = parse(value, "h:mm a", new Date());
    return isValid(parsed) ? parsed : undefined;
}

function parseTimeConstraint(value: string): Date | undefined {
    const parsed = parse(value, "HH:mm:ss", new Date());
    return isValid(parsed) ? parsed : undefined;
}

function parseTimeConstraintFromDateTimeString(value: string): Date | undefined {
    const timePortion = value.split("T")[1];
    return timePortion ? parseTimeConstraint(timePortion) : undefined;
}

function evaluateTimeOfDayPredicate(predicate: DatePredicateProperties & SurveyResultPredicateBase, managedResults: ManagedResultsLookup): boolean {
    const questionResult = getResult(predicate, managedResults);
    if (!isQuestionResult(questionResult)) {
        return false;
    }

    const answer = questionResult.Answers[0];
    if (isBlankAnswer(answer)) {
        return false;
    }

    const parsedAnswerTime = parseTimeAnswer(answer);
    if (!parsedAnswerTime) {
        return false;
    }

    if (predicate.maximumExpectedDate !== undefined) {
        if (typeof predicate.maximumExpectedDate !== "string" && relativeDateIsValid(predicate.maximumExpectedDate)) {
            const maximumRelativeDate = dateTimeFromDateObjectOrString(predicate.maximumExpectedDate);
            const parsedMaximumRelativeTime = maximumRelativeDate ? parseTimeAnswer(format(maximumRelativeDate, "h:mm a")) : undefined;
            if (parsedMaximumRelativeTime && parsedAnswerTime && isAfter(parsedAnswerTime, parsedMaximumRelativeTime)) {
                return false;
            }
        } else if (typeof predicate.maximumExpectedDate === "string") {
            const parsedMaximumTime = parseTimeConstraintFromDateTimeString(predicate.maximumExpectedDate);
            if (parsedMaximumTime && parsedAnswerTime && isAfter(parsedAnswerTime, parsedMaximumTime)) {
                return false;
            }
        }
    }

    if (predicate.minimumExpectedDate !== undefined) {
        if (typeof predicate.minimumExpectedDate !== "string" && relativeDateIsValid(predicate.minimumExpectedDate)) {
            const minimumRelativeDate = dateTimeFromDateObjectOrString(predicate.minimumExpectedDate);
            const parsedMinimumRelativeTime = minimumRelativeDate ? parseTimeAnswer(format(minimumRelativeDate, "h:mm a")) : undefined;
            if (parsedMinimumRelativeTime && parsedAnswerTime && isAfter(parsedMinimumRelativeTime, parsedAnswerTime)) {
                return false;
            }
        } else if (typeof predicate.minimumExpectedDate === "string") {
            const parsedMinimumTime = parseTimeConstraintFromDateTimeString(predicate.minimumExpectedDate);
            if (parsedMinimumTime && parsedAnswerTime && isAfter(parsedMinimumTime, parsedAnswerTime)) {
                return false;
            }
        }
    }

    return true;
}

function unsupportedPredicateType(predicate: { type: string }): never {
    throw new Error(`Unsupported Predicate type: ${predicate.type}`);
}

export function evaluatePredicate(
    predicate: SurveyPredicate,
    managedResults: ManagedResultsLookup,
    predicateExpressionEvaluator: (expression: string) => boolean): boolean {
    switch (predicate.type) {
        case "TruePredicate":
            return true;
        case "FalsePredicate":
            return false;
        case "BooleanQuestionResultPredicate": {
            const questionResult = getResult(predicate, managedResults);
            if (!questionResult) {
                return false;
            }

            if (!isQuestionResult(questionResult)) {
                return false;
            }

            const answer = questionResult.Answers[0];
            if (isBlankAnswer(answer)) {
                return false;
            }

            return predicate.expectedAnswer === (answer === "true" || answer === "True");
        }
        case "ConsentPredicate": {
            const questionResult = getResult(predicate, managedResults);
            if (!isConsentSignatureResult(questionResult)) {
                return false;
            }
            return predicate.didConsent === questionResult.Consented;
        }
        case "WebViewResultPredicate": {
            const hasExpectedString = "expectedString" in predicate;
            const hasMatchingPattern = "matchingPattern" in predicate;
            if (hasExpectedString === hasMatchingPattern) {
                return false;
            }

            const webViewResult = getResult(predicate, managedResults);
            if (!isWebViewStepResult(webViewResult)) {
                return false;
            }

            if (hasExpectedString) {
                return webViewResult.Result === predicate.expectedString;
            }

            const regex = new RegExp(predicate.matchingPattern);
            return regex.test(webViewResult.Result ?? "");
        }
        case "NumericQuestionResultPredicate":
        case "ScaleQuestionResultPredicate":
        case "HeightWeightQuestionResultPredicate":
            return evaluateNumericPredicate(predicate, managedResults);
        case "TextQuestionResultPredicate": {
            const questionResult = getResult(predicate, managedResults);
            if (!isQuestionResult(questionResult)) {
                return false;
            }
            return questionResult.Answers[0] === predicate.expectedString;
        }
        case "ChoiceQuestionResultPredicate": {
            const questionResult = getResult(predicate, managedResults);
            if (!isQuestionResult(questionResult)) {
                return false;
            }

            let result = false;
            if (Object.prototype.hasOwnProperty.call(predicate, "expectedString") && predicate.expectedString !== undefined) {
                result = questionResult.Answers.includes(predicate.expectedString);
            }
            if (Object.prototype.hasOwnProperty.call(predicate, "expectedStrings") && predicate.expectedStrings !== undefined) {
                result = predicate.expectedStrings.every(expectedString => questionResult.Answers.includes(expectedString));
            }
            return result;
        }
        case "DateQuestionResultPredicate":
            return evaluateDatePredicate(predicate, managedResults);
        case "TimeOfDayQuestionResultPredicate":
            return evaluateTimeOfDayPredicate(predicate, managedResults);
        case "NilQuestionResultPredicate": {
            const questionResult = getResult(predicate, managedResults);
            if (!questionResult) {
                return true;
            }

            if ("Answers" in questionResult) {
                const answers = questionResult.Answers;
                return answers === null || (Array.isArray(answers) && answers.every(isBlankAnswer));
            }

            if ("File" in questionResult) {
                const file = questionResult.File;
                return file === undefined || file === "" || file === null;
            }

            return true;
        }
        case "NotCompoundPredicate":
            return !evaluatePredicate(predicate.subpredicate, managedResults, predicateExpressionEvaluator);
        case "OrCompoundPredicate":
            return predicate.subpredicates.some(subpredicate => evaluatePredicate(subpredicate, managedResults, predicateExpressionEvaluator));
        case "AndCompoundPredicate":
            return predicate.subpredicates.every(subpredicate => evaluatePredicate(subpredicate, managedResults, predicateExpressionEvaluator));
        case "ExpressionPredicate":
            return predicate.expression === "True";
        case "CommonExpressionPredicate":
            return predicateExpressionEvaluator(predicate.expression?.expression);
        case "CustomFieldPredicate":
        case "DemographicFieldPredicate":
        case "InstitutionPredicate":
        case "ParticipantsInSegmentPredicate":
            return false; // these are replaced server-side, and do not evaluate client-side
        default:
            return unsupportedPredicateType(predicate);
    }
}

// --- Predicate Description ---

function bold(value: string | number | boolean | undefined): string {
    return "<strong>" + value + "</strong>";
}

function formatResultIdentifier(predicate: SurveyResultPredicateBase): string {
    if (predicate.stepIdentifier) {
        return bold(predicate.stepIdentifier) + " > " + bold(predicate.resultIdentifier);
    }
    return bold(predicate.resultIdentifier);
}

function dateStringFromDateObject(dateObjOrString: RelativeDateConstraint | string | undefined, friendlyFormat: string): string | undefined {
    if (!dateObjOrString) return undefined;
    if (typeof dateObjOrString === "object" && dateObjOrString.type === "RelativeDate") {
        const r = dateObjOrString;
        let offsetValue = 0;
        let offsetModifier = "day";
        if (r.offsetYears !== 0) {
            offsetValue = r.offsetYears;
            offsetModifier = "year";
        } else if (r.offsetMonths !== 0) {
            offsetValue = r.offsetMonths;
            offsetModifier = "month";
        } else if (r.offsetWeeks !== 0) {
            offsetValue = r.offsetWeeks;
            offsetModifier = "week";
        } else if (r.offsetDays !== 0) {
            offsetValue = r.offsetDays;
            offsetModifier = "day";
        } else if (r.offsetHours !== 0) {
            offsetValue = r.offsetHours;
            offsetModifier = "hour";
        } else if (r.offsetMinutes !== 0) {
            offsetValue = r.offsetMinutes;
            offsetModifier = "minute";
        }
        const modifier = offsetValue < 0 ? "in the past" : "in the future";
        offsetValue = Math.abs(offsetValue);
        if (offsetValue !== 1) {
            offsetModifier += "s";
        }
        let result = offsetValue + " " + offsetModifier + " " + modifier;
        result += r.fromStartOfDay === "true" || r.fromStartOfDay === true ? " from start of day" : " from current date/time";
        return result;
    }
    return format(parseISO(dateObjOrString as string), friendlyFormat);
}

function describeDatePredicate(predicate: DateQuestionResultPredicate | TimeOfDayQuestionResultPredicate, friendlyFormat: string): string {
    const { minimumExpectedDate, maximumExpectedDate } = predicate;
    if (minimumExpectedDate !== undefined && maximumExpectedDate !== undefined) {
        return formatResultIdentifier(predicate) + " is on or after " + bold(dateStringFromDateObject(minimumExpectedDate, friendlyFormat)) + " and on or before " + bold(dateStringFromDateObject(maximumExpectedDate, friendlyFormat));
    } else if (minimumExpectedDate !== undefined) {
        return formatResultIdentifier(predicate) + " is on or after " + bold(dateStringFromDateObject(minimumExpectedDate, friendlyFormat));
    } else if (maximumExpectedDate !== undefined) {
        return formatResultIdentifier(predicate) + " is on or before " + bold(dateStringFromDateObject(maximumExpectedDate, friendlyFormat));
    }
    return formatResultIdentifier(predicate) + "...";
}

function describeNumericPredicate(predicate: NumericQuestionResultPredicate | ScaleQuestionResultPredicate | HeightWeightQuestionResultPredicate): string {
    if (predicate.minimumExpectedAnswerValue !== undefined && predicate.maximumExpectedAnswerValue !== undefined) {
        return formatResultIdentifier(predicate) + " is greater than or equal to " + bold(predicate.minimumExpectedAnswerValue) + " and less than or equal to " + bold(predicate.maximumExpectedAnswerValue);
    } else if (predicate.minimumExpectedAnswerValue !== undefined) {
        return formatResultIdentifier(predicate) + " is greater than or equal to " + bold(predicate.minimumExpectedAnswerValue);
    } else if (predicate.maximumExpectedAnswerValue !== undefined) {
        return formatResultIdentifier(predicate) + " is less than or equal to " + bold(predicate.maximumExpectedAnswerValue);
    } else {
        return formatResultIdentifier(predicate) + " is equal to " + bold(predicate.expectedAnswer);
    }
}

function describePredicateInner(predicate: SurveyPredicate): string {
    if (!predicate?.type) {
        return "";
    }
    switch (predicate.type) {
        case "TruePredicate":
            return bold("true");
        case "FalsePredicate":
            return bold("false");
        case "BooleanQuestionResultPredicate":
            return formatResultIdentifier(predicate) + " is " + bold(predicate.expectedAnswer ? "Yes" : "No");
        case "ConsentPredicate":
            return predicate.didConsent
                ? formatResultIdentifier(predicate) + " was consented to"
                : formatResultIdentifier(predicate) + " was NOT consented to";
        case "WebViewResultPredicate":
            return predicate.expectedString !== undefined
                ? formatResultIdentifier(predicate) + " is " + bold(predicate.expectedString)
                : formatResultIdentifier(predicate) + " matches pattern " + bold(predicate.matchingPattern);
        case "NumericQuestionResultPredicate":
        case "ScaleQuestionResultPredicate":
        case "HeightWeightQuestionResultPredicate":
            return describeNumericPredicate(predicate);
        case "TextQuestionResultPredicate":
            return formatResultIdentifier(predicate) + " is " + bold(predicate.expectedString);
        case "ChoiceQuestionResultPredicate":
            if (predicate.expectedString !== undefined) {
                return formatResultIdentifier(predicate) + " is " + bold(predicate.expectedString);
            } else {
                return formatResultIdentifier(predicate) + " contains all of [" + bold(predicate.expectedStrings.join(", ")) + "]";
            }
        case "DateQuestionResultPredicate":
            return describeDatePredicate(predicate, "MMM d yyyy, h:mm a");
        case "TimeOfDayQuestionResultPredicate":
            return describeDatePredicate(predicate, "h:mm a");
        case "NilQuestionResultPredicate":
            return formatResultIdentifier(predicate) + " is empty (skipped)";
        case "CustomFieldPredicate":
            return "CustomField." + bold(predicate.customField) + " is " + (predicate.operator === "IsEmpty" ? bold("empty") : bold(predicate.expectedValue));
        case "DemographicFieldPredicate":
            if (predicate.demographicField === "Age") {
                if (predicate.operator === "Between") {
                    return "DemographicField." + bold(predicate.demographicField) + " is between " + bold(predicate.minimumExpectedValue) + " and " + bold(predicate.maximumExpectedValue);
                } else if (predicate.operator === "Minimum") {
                    return "DemographicField." + bold(predicate.demographicField) + " is at least " + bold(predicate.minimumExpectedValue);
                } else if (predicate.operator === "Maximum") {
                    return "DemographicField." + bold(predicate.demographicField) + " is at most " + bold(predicate.maximumExpectedValue);
                } else {
                    return "DemographicField." + bold(predicate.demographicField) + " is equal to " + bold(predicate.expectedValue);
                }
            } else {
                return "DemographicField." + bold(predicate.demographicField) + " is " + (predicate.operator === "IsEmpty" ? bold("empty") : bold(predicate.expectedValue));
            }
        case "InstitutionPredicate":
            return "Institution is " + bold(predicate.operator === "IsEmpty" ? "empty" : predicate.expectedValue);
        case "ExpressionPredicate": {
            const stripped = predicate.expression.replace("<%=", "").replace("%>", "").trim();
            return "Expression " + bold(stripped) + " is " + bold("true");
        }
        case "ParticipantsInSegmentPredicate": {
            const { minimumExpectedValue: min, maximumExpectedValue: max } = predicate;
            if (min !== undefined && max !== undefined) {
                return "Participants in segment " + bold(predicate.segment) + " is greater than or equal to " + bold(min) + " and less than or equal to " + bold(max);
            } else if (min !== undefined) {
                return "Participants in segment " + bold(predicate.segment) + " is greater than or equal to " + bold(min);
            } else if (max !== undefined) {
                return "Participants in segment " + bold(predicate.segment) + " is less than or equal to " + bold(max);
            }
            return "Participants in segment " + bold(predicate.segment) + "...";
        }
        case "CommonExpressionPredicate":
            return "Expression " + bold(predicate.expression?.expression) + " is " + bold("true");
        case "NotCompoundPredicate":
            return "The following condition is <strong>not</strong> met:\n" + getPredicateDescriptionHtml(predicate.subpredicate);
        case "OrCompoundPredicate":
            return "One of the following conditions is met:\n" + predicate.subpredicates.map(getPredicateDescriptionHtml).join("");
        case "AndCompoundPredicate":
            return "All of the following conditions are met:\n" + predicate.subpredicates.map(getPredicateDescriptionHtml).join("");
        default:
            return unsupportedPredicateType(predicate);
    }
}

export function getPredicateDescriptionHtml(predicate: SurveyPredicate | { type?: string }): string {
    if (!predicate?.type) {
        return "";
    }
    return "<div class='predicate'>" + describePredicateInner(predicate as SurveyPredicate) + "</div>";
}
